import { useCallback, useEffect, useState } from 'react';
import projectService from '../services/projectService';
import { buildProjectPayload, hydrateEditorState } from '../utils/payloadFormatter';
import { newProjectMeta } from '../constants/defaultConfig';

// ─── Report data lives in the project config saved to the DB ──────────────────
// These helpers read/write report data from localStorage only as a staging area
// between modal-close and the next project save. They are NOT the source of truth —
// the DB config.reports field is.
//
// Every one of these editors (VastuDevtaGuide, SpaceAnalysis, EntranceAnalysis,
// VastuPersonsGateway/BirthDateModal, CutExtensionGuide, NavgrahShanti, FengShui,
// ZoneAnalysis) keys its own localStorage entry as `${prefix}_${projectId}` —
// mirror that exact naming here so this bridge reads/writes the same keys the
// editors themselves use, per project.
const safeParseLocal = (key) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
};

// { reportsConfigKey: localStoragePrefix }
const REPORT_KEY_PREFIXES = {
  vastuDevtaGuide:     'vastu_devta_guide_data',
  spaceAnalysis:        'space_analysis_report_data',
  entranceAnalysis:     'entrance_analysis_report_data',
  vastuPersons:         'vastu_persons_data',
  numeroVastu:          'numero_vastu_data',
  numeroVastuKua:       'numero_vastu_kua_data',
  astroVastu:           'astro_vastu_data',
  cutZones:             'cut_zone_data',
  extensionZones:       'extension_zone_data',
  navgrahShanti:        'navgrah_shanti_data',
  fengShui:             'feng_shui_data',
  helix:                'helix_data',
  zoneAnalysis:         'zone_analysis_data',
};

const readReportData = (activeId) => {
  const suffix = activeId || 'new';
  const out = {};
  for (const [field, prefix] of Object.entries(REPORT_KEY_PREFIXES)) {
    out[field] = safeParseLocal(`${prefix}_${suffix}`);
  }
  return out;
};

const restoreReportData = (reports = {}, activeId) => {
  const suffix = activeId || 'new';
  const write = (key, val) => {
    try {
      if (val) localStorage.setItem(key, JSON.stringify(val));
      else localStorage.removeItem(key);
    } catch {}
  };
  for (const [field, prefix] of Object.entries(REPORT_KEY_PREFIXES)) {
    write(`${prefix}_${suffix}`, reports[field] ?? null);
  }
};

// ─── useProject ───────────────────────────────────────────────────────────────
// Owns the project lifecycle. All persistence goes through the API — no
// localStorage fallback for project data.
export default function useProject({ snapshot, applySetters, externalProjectId = null }) {
  const [currentProject, setCurrentProject]           = useState(null);
  // The id every localStorage report key is currently scoped under: the saved
  // project's own id once one exists, otherwise whatever id the caller (a
  // pre-assigned draft id, if any) was opened with — matches the same
  // `currentProject?.id || projectId` fallback used throughout Configure.
  const activeStorageId = currentProject?.id || externalProjectId;
  const [projects, setProjects]                       = useState([]);
  const [loading, setLoading]                         = useState(false);
  const [error, setError]                             = useState(null);
  const [savedSnapshot, setSavedSnapshot]             = useState(null);
  const [savedReportSnapshot, setSavedReportSnapshot] = useState(null);
  // { done, total } while report-layer images are uploading in the background
  // after a save, or null when nothing is syncing. The save modal closes as
  // soon as the main save request resolves — it does not wait for this.
  const [layerImagesSyncProgress, setLayerImagesSyncProgress] = useState(null);

  // Runs the chunked layer-image upload and tracks progress. AWAITED by
  // saveProject/createProject below (previously fire-and-forget — the main
  // save request resolved instantly while this kept trickling in behind it).
  // That let a user close/reload the tab right after seeing "Saved" while
  // some newly-enabled reports' images were still mid-upload, silently
  // losing them — reported directly by the client: a report saved once
  // should "hamesha ke liye save rahe jab tak user use khud close na kare"
  // (stay saved forever until the user closes it themselves). Awaiting here
  // means Save genuinely isn't done — and the Save modal's spinner doesn't
  // close — until every image has actually landed in the DB.
  const syncLayerImages = useCallback(async (projectId, fullLayerImages) => {
    const total = Math.ceil(Object.keys(fullLayerImages || {}).length / 10);
    if (total === 0) return;
    setLayerImagesSyncProgress({ done: 0, total });
    try {
      await projectService.saveLayerImagesInChunks(projectId, fullLayerImages, (done, totalChunks) => {
        setLayerImagesSyncProgress({ done, total: totalChunks });
      });
    } finally {
      setLayerImagesSyncProgress(null);
    }
  }, []);

  const isDirty = currentProject !== null && savedSnapshot !== null
    && (JSON.stringify(snapshot) !== savedSnapshot
      || (savedReportSnapshot !== null
          && JSON.stringify(readReportData(activeStorageId)) !== savedReportSnapshot));

  // ── list ──────────────────────────────────────────────────────────────────
  const listProjects = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await projectService.list();
      const items = Array.isArray(response) ? response : (response?.data ?? []);
      setProjects(items);
      return items;
    } catch (e) {
      setError(e.message || 'Failed to load projects');
      setProjects([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ── get / load ────────────────────────────────────────────────────────────
  const loadProject = useCallback(async (id) => {
    setLoading(true); setError(null);
    try {
      const response = await projectService.get(id);
      const project  = response?.data ?? response;

      // Mirrors projectService.js's SAVE_PAYLOAD_DEBUG log — lets us see
      // straight from the console whether report data (e.g. vastuPersons)
      // actually came back from the server on load, the same way we can
      // already see whether it went out on save.
      // eslint-disable-next-line no-console
      console.log('LOAD_PAYLOAD_DEBUG', {
        projectId: project?.id,
        configType: typeof project?.config,
        reports: project?.config?.reports,
      });

      const hydrated = hydrateEditorState(project);

      // Migrate old saved layer configs opacity for Vedic layers
      const VEDIC_70_OPACITY_LAYERS = new Set([
        'vastu-numbers-81-numbers-Vedic', 'Vedic-Mahavastu-Chakra',
        '16-Directional-Domestic-Vastu', 'Khanija-Dhatu',
        'Vedic-devata-Numbers-with-Names-81', 'Direction-Diety-Planet',
        'Devata-Khanij', 'Devata-Bhojan', 'Devata-Bhojan-aadi',
        'Devata-chinha', 'Devata-chinha-aadi', 'Muhurat-vichar',
        'Seharumkh-achintya-vibhav', 'Disha-Gandha', 'Devata-Nighat', 'Nighantu',
      ]);
      if (hydrated?.savedLayerConfigs) {
        const migrated = { ...hydrated.savedLayerConfigs };
        for (const layerId of Object.keys(migrated)) {
          if (VEDIC_70_OPACITY_LAYERS.has(layerId) && migrated[layerId]) {
            migrated[layerId] = { ...migrated[layerId], opacity: 0.7 };
          }
        }
        hydrated.savedLayerConfigs = migrated;
      }

      applySetters(hydrated);
      // Restore report data from DB config into localStorage (for modal components to read)
      restoreReportData(project.config?.reports ?? {}, project.id);

      setCurrentProject({
        id:           project.id,
        name:         project.name,
        floorPlanRef: project.floorPlanRef ?? null,
        createdAt:    project.createdAt,
        updatedAt:    project.updatedAt,
      });
      setSavedSnapshot(JSON.stringify(hydrated));
      setSavedReportSnapshot(JSON.stringify(readReportData(project.id)));
      return project;
    } catch (e) {
      setError(e.message || 'Failed to load project');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [applySetters]);

  // ── create ────────────────────────────────────────────────────────────────
  const createProject = useCallback(async (name, floorPlanRef = null, clientDetails = null) => {
    setLoading(true); setError(null);
    const preSaveId = activeStorageId; // id the report editors were writing under before this project existed in the DB
    // clientDetails (Property/Client Details collected on the "Create Project"
    // form) is passed straight into meta instead of round-tripping through
    // setClientDetails() first — see buildProjectPayload's comment on why.
    const meta    = { ...newProjectMeta(name), floorPlanRef, reportData: readReportData(preSaveId), clientDetails: clientDetails ?? undefined };
    const payload = buildProjectPayload(snapshot, meta);
    // Report-layer images travel separately, chunked, after the main save —
    // see the comment on saveProject below for why.
    const fullLayerImages = payload.config?.layers?.layerImages ?? {};
    payload.config = { ...payload.config, layers: { ...payload.config.layers, layerImages: {} } };
    try {
      const response = await projectService.create(payload);
      const saved    = response?.data ?? response;

      // Awaited — see syncLayerImages' comment. Save isn't "done" until the
      // report images this project needs are actually durable in the DB.
      await syncLayerImages(saved.id, fullLayerImages);

      // Re-key the report editors' localStorage entries from the pre-save id
      // (e.g. "new") onto the real, now-permanent project id so the very next
      // read/write (and the next save's isDirty check) lines up.
      if (saved.id && saved.id !== preSaveId) {
        restoreReportData(meta.reportData, saved.id);
      }

      setCurrentProject({
        id:           saved.id,
        name:         saved.name,
        floorPlanRef: saved.floorPlanRef ?? floorPlanRef ?? null,
        createdAt:    saved.createdAt,
        updatedAt:    saved.updatedAt,
      });
      setSavedSnapshot(JSON.stringify(snapshot));
      setSavedReportSnapshot(JSON.stringify(readReportData(saved.id)));
      return saved;
    } catch (e) {
      setError(e.message || 'Failed to create project');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [snapshot, syncLayerImages, activeStorageId]);

  // ── save (edit if exists, create otherwise) ───────────────────────────────
  const saveProject = useCallback(async (nameOverride, floorPlanRef) => {
    if (!currentProject?.id) {
      return createProject(nameOverride || 'Untitled Project', floorPlanRef ?? null);
    }

    setLoading(true); setError(null);
    const resolvedFloorPlan = floorPlanRef ?? currentProject.floorPlanRef ?? null;
    const meta = {
      ...currentProject,
      name:         nameOverride || currentProject.name,
      floorPlanRef: resolvedFloorPlan,
      updatedAt:    new Date().toISOString(),
      reportData:   readReportData(currentProject.id),
    };
    const payload = buildProjectPayload(snapshot, meta);
    // With many enabled report layers, config.layers.layerImages alone can run
    // into tens of MB of base64 (every layer's rendered PNG, still uncompressed
    // in memory until this save round-trips through the backend's file-offload
    // step). Sending it all in one request is exactly what silently fails —
    // Chrome's own Network panel stops showing the payload past a certain size
    // ("Provisional headers are shown"), and the same oversized body trips the
    // WAF/PHP post-size limit server-side, so the save errors out. Splitting it
    // into small sequential chunks keeps every single request tiny regardless
    // of how many reports are enabled.
    const fullLayerImages = payload.config?.layers?.layerImages ?? {};
    payload.config = { ...payload.config, layers: { ...payload.config.layers, layerImages: {} } };
    try {
      const response = await projectService.edit(currentProject.id, payload);
      const saved    = response?.data ?? response;

      // Awaited — see syncLayerImages' comment. Save isn't "done" until the
      // report images this project needs are actually durable in the DB.
      await syncLayerImages(currentProject.id, fullLayerImages);

      setCurrentProject({
        ...meta,
        floorPlanRef: saved?.floorPlanRef ?? resolvedFloorPlan,
        updatedAt:    saved?.updatedAt    ?? meta.updatedAt,
      });
      setSavedSnapshot(JSON.stringify(snapshot));
      setSavedReportSnapshot(JSON.stringify(readReportData(currentProject.id)));
      return saved ?? meta;
    } catch (e) {
      setError(e.message || 'Failed to save project');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [currentProject, snapshot, createProject, syncLayerImages]);

  // ── delete ────────────────────────────────────────────────────────────────
  const deleteProject = useCallback(async (id) => {
    setLoading(true); setError(null);
    try {
      await projectService.delete(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (currentProject?.id === id) {
        setCurrentProject(null);
        setSavedSnapshot(null);
        setSavedReportSnapshot(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to delete project');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  // ── new project (reset state, no persistence) ─────────────────────────────
  const startNewProject = useCallback(() => {
    setCurrentProject(null);
    setSavedSnapshot(null);
    setSavedReportSnapshot(null);
    setError(null);
  }, []);

  useEffect(() => { /* intentionally empty */ }, []);

  return {
    currentProject,
    projects,
    loading,
    error,
    isDirty,
    layerImagesSyncProgress,
    listProjects,
    loadProject,
    createProject,
    saveProject,
    deleteProject,
    startNewProject,
  };
}
